// src/hooks/useWeather.js — Broadway Holidays
// Live weather via Open-Meteo (free, no API key required, CORS-enabled)
// Docs: https://open-meteo.com/en/docs
import { useState, useEffect } from "react";

// WMO weather codes → simplified condition buckets
function classifyCode(code) {
  if (code === 0) return "clear";
  if ([1, 2].includes(code)) return "partly-cloudy";
  if (code === 3) return "overcast";
  if ([45, 48].includes(code)) return "fog";
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return "rain";
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "snow";
  if ([95, 96, 99].includes(code)) return "storm";
  return "cloudy";
}

/**
 * @param {Array} places - [{ name, lat, lon }]
 * @returns {Object} map of name -> { temp, condition } | undefined while loading
 */
export default function useWeather(places) {
  const [weather, setWeather] = useState({});

  useEffect(() => {
    if (!places || places.length === 0) return;
    let cancelled = false;

    const lats = places.map(p => p.lat).join(",");
    const lons = places.map(p => p.lon).join(",");
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lats}&longitude=${lons}&current=temperature_2m,weather_code&timezone=auto`;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        const results = Array.isArray(data) ? data : [data];
        const map = {};
        results.forEach((r, i) => {
          const place = places[i];
          if (place && r?.current) {
            map[place.name] = {
              temp: Math.round(r.current.temperature_2m),
              condition: classifyCode(r.current.weather_code),
            };
          }
        });
        setWeather(map);
      })
      .catch(() => {
        // Silently fail — weather chips just won't render if the API is unreachable
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places.map(p => p.name).join(",")]);

  return weather;
}